import * as Sentry from '@sentry/browser'

// ViewHook interface to match Phoenix LiveView hook context
export interface ViewHook {
  el: HTMLElement
  pushEvent: (
    event: string,
    payload: Record<string, unknown>,
    onReply?: (reply: unknown, ref: number) => unknown
  ) => unknown
  pushEventTo: (
    phxTarget: string,
    event: string,
    payload: Record<string, unknown>,
    onReply?: (reply: unknown, ref: number) => unknown
  ) => unknown
}

/**
 * LiveView Push Error
 */
export class LiveViewPushError extends Error {
  constructor(
    public readonly event: string,
    message: string,
    public readonly payload?: Record<string, unknown>
  ) {
    super(message)
    this.name = 'LiveViewPushError'
  }
}

/**
 * Promise-based LiveView event push with error handling
 * Replaces Effect.ts with simpler Promise-based approach
 */
export const pushToLiveView = <T = unknown>(
  hook: ViewHook,
  event: string,
  payload: Record<string, unknown> = {},
  target?: string
): Promise<T> =>
  new Promise((resolve, reject) => {
    // Add breadcrumb for debugging
    if (process.env.NODE_ENV === 'production') {
      Sentry.addBreadcrumb({
        category: 'liveview',
        message: `Pushing event: ${event}`,
        level: 'info',
        data: {
          event,
          target,
          payloadKeys: Object.keys(payload),
          component: hook.el?.dataset?.component || hook.el?.tagName
        }
      })
    }

    const pushFn = target
      ? (cb: (reply: unknown) => void) => {
          try {
            return hook.pushEventTo(target, event, payload, cb)
          } catch (error) {
            // Capture push function error for monitoring
            captureEffectError(error as Error, {
              component: hook.el?.dataset?.component || hook.el?.tagName,
              hookElement: hook.el,
              event,
              payload,
              target,
              operation: 'pushToLiveView:pushEventTo'
            })

            // Create a LiveViewPushError and reject
            reject(
              new LiveViewPushError(
                event,
                `Push function failed: ${String(error)}`,
                payload
              )
            )
            return
          }
        }
      : (cb: (reply: unknown) => void) => {
          try {
            return hook.pushEvent(event, payload, cb)
          } catch (error) {
            // Capture push function error for monitoring
            captureEffectError(error as Error, {
              component: hook.el?.dataset?.component || hook.el?.tagName,
              hookElement: hook.el,
              event,
              payload,
              target,
              operation: 'pushToLiveView:pushEvent'
            })

            // Create a LiveViewPushError and reject
            reject(
              new LiveViewPushError(
                event,
                `Push function failed: ${String(error)}`,
                payload
              )
            )
            return
          }
        }

    pushFn((reply: unknown) => {
      if (
        reply &&
        typeof reply === 'object' &&
        (reply as Record<string, unknown>).error
      ) {
        const errorReply = reply as Record<string, unknown>
        const error = new LiveViewPushError(
          event,
          String(errorReply.error),
          payload
        )

        // Capture LiveView push error for monitoring
        captureEffectError(error, {
          component: hook.el?.dataset?.component || hook.el?.tagName,
          hookElement: hook.el,
          event,
          payload,
          target,
          operation: 'pushToLiveView'
        })

        reject(error)
      } else {
        resolve(reply as T)
      }
    })
  })

/**
 * Upload Error
 */
export class UploadError extends Error {
  constructor(
    public readonly fileName: string,
    public readonly fileSize: number,
    message: string,
    public readonly status?: number,
    public readonly cause?: unknown
  ) {
    super(message)
    this.name = 'UploadError'
  }
}

/**
 * Sentry error capture with LiveView context
 */
export const captureEffectError = (
  error: LiveViewPushError | UploadError | Error | unknown,
  context: {
    component?: string
    hookElement?: HTMLElement
    event?: string
    payload?: Record<string, unknown>
    target?: string
    operation?: string
  }
) => {
  // Only capture in production
  if (process.env.NODE_ENV !== 'production') {
    return
  }

  // Extract component information from hook element
  const componentInfo = context.hookElement
    ? {
        componentId: context.hookElement.id || 'unknown',
        componentClass: context.hookElement.className || 'unknown',
        componentDataset: Object.keys(context.hookElement.dataset).reduce(
          (acc, key) => {
            // Only include relevant data attributes, not sensitive data
            if (
              key.startsWith('component') ||
              key.startsWith('phx') ||
              key === 'id'
            ) {
              acc[key] = context.hookElement!.dataset[key]
            }
            return acc
          },
          {} as Record<string, string | undefined>
        )
      }
    : {}

  // Determine error type and severity
  let level: Sentry.SeverityLevel = 'error'
  let fingerprint: string[] = ['liveview-error']

  if (error instanceof LiveViewPushError) {
    fingerprint = ['liveview-push', error.event]
    level = 'error'
  } else if (error instanceof UploadError) {
    fingerprint = ['upload-error', error.fileName]
    level = 'error'
  } else if (error && typeof error === 'object' && '_tag' in error) {
    // Handle Effect.ts Tagged errors (including grid errors)
    const taggedError = error as {
      _tag: string
      operation?: string
      gridId?: string
      page?: number
      [key: string]: unknown
    }
    if (taggedError._tag === 'GridDataOperationError') {
      fingerprint = [
        'grid-data-operation',
        String(taggedError.operation),
        String(taggedError.gridId)
      ]
      level = 'error'
    } else if (taggedError._tag === 'GridProgressiveLoadError') {
      fingerprint = [
        'grid-progressive-load',
        String(taggedError.page),
        String(taggedError.gridId)
      ]
      level = 'error'
    } else {
      fingerprint = ['effect-tagged-error', taggedError._tag]
      level = 'error'
    }
  }

  // Capture the error with rich context
  Sentry.captureException(error, {
    level,
    fingerprint,
    tags: {
      component: context.component || 'unknown',
      operation: context.operation || 'unknown',
      event: context.event || 'unknown',
      ...(context.target && { target: context.target })
    },
    contexts: {
      liveview: {
        event: context.event,
        payload: context.payload,
        target: context.target,
        ...componentInfo
      }
    },
    extra: {
      errorDetails:
        error && typeof error === 'object' && '_tag' in error
          ? {
              ...error,
              _tag: (error as { _tag: string })._tag
            }
          : {
              message: (error as { message?: unknown })?.message
                ? String((error as { message?: unknown }).message)
                : String(error),
              stack: (error as { stack?: unknown })?.stack
                ? String((error as { stack?: unknown }).stack)
                : undefined
            }
    }
  })
}

/**
 * Simplified Effect.ts utilities for common LiveView patterns
 */

/**
 * Simple push with callback - most common pattern
 * Reduces boilerplate for the standard push-then-callback flow
 */
export const pushAndCall = <T>(
  hook: ViewHook,
  event: string,
  payload: Record<string, unknown>,
  callback: (reply: T) => void,
  target?: string,
  _errorMessage?: string
) => {
  pushToLiveView<T>(hook, event, payload, target)
    .then(callback)
    .catch((error) => {
      captureEffectError(error, {
        component: hook.el?.dataset?.component || hook.el?.tagName,
        hookElement: hook.el,
        event,
        payload,
        target,
        operation: 'pushAndCall'
      })
    })
}

/**
 * Fire-and-forget push - for events that don't need response handling
 * Perfect for notifications, logging, simple state updates
 */
export const pushEvent = (
  hook: ViewHook,
  event: string,
  payload: Record<string, unknown> = {},
  target?: string,
  _errorMessage?: string
) => {
  pushToLiveView(hook, event, payload, target).catch((error) => {
    captureEffectError(error, {
      component: hook.el?.dataset?.component || hook.el?.tagName,
      hookElement: hook.el,
      event,
      payload,
      target,
      operation: 'pushEvent'
    })
  })
}

/**
 * Safe form trigger - simplified form event triggering with error handling
 */
export const triggerForm = (
  inputId: string,
  event: 'input' | 'change' = 'input',
  _errorMessage?: string,
  hookElement?: HTMLElement
) => {
  try {
    const inputEl = document.getElementById(inputId)
    if (!inputEl) {
      // The carrier may have been removed during a morphdom patch or teardown.
      return
    }

    // Dispatch the event on the input element
    const eventObj = new Event(event, { bubbles: true, cancelable: true })
    inputEl.dispatchEvent(eventObj)
  } catch (error) {
    captureEffectError(error as Error, {
      component: hookElement?.dataset?.component || 'form',
      hookElement,
      event: `form:${event}`,
      payload: { inputId },
      operation: 'triggerForm'
    })
  }
}

/**
 * Debounce Promise operations with cancellation and composability
 *
 * ## Timing Modes
 *
 * ### trailing (default)
 * Executes AFTER the quiet period (no calls for waitMs). Each call restarts the timer.
 * - Good for: Form validation, search inputs, window resize handlers
 * - Pattern: type-type-type → [wait] → execute once
 *
 * ### leading
 * Executes IMMEDIATELY on first call, then blocks subsequent calls until quiet period ends.
 * - Returns cached result for rapid calls during quiet period (intentional behavior)
 * - Good for: Submit buttons, API calls that shouldn't repeat
 * - Pattern: click → execute immediately → [blocks for waitMs] → click again → execute
 *
 * ### both
 * Executes IMMEDIATELY (leading) AND AFTER quiet period (trailing).
 * - Leading execution returns a Promise that resolves with the result
 * - Trailing execution runs independently and doesn't propagate errors to leading Promise
 * - If trailing execution fails, error is captured to Sentry but doesn't reject the leading Promise
 * - Good for: Real-time updates with immediate feedback + server sync
 * - Pattern: type → execute → [keep typing] → execute again after quiet
 *
 * ## Important Behavior Notes
 *
 * **Leading Edge Caching**: When using 'leading' timing, rapid calls during the quiet period
 * will receive the SAME cached result from the first execution. This prevents expensive
 * operations from running multiple times but means callers might receive stale data.
 *
 * **Trailing Error Handling in "Both" Mode**: The trailing execution in "both" mode runs
 * independently. Errors from the trailing execution are captured to Sentry but do NOT
 * propagate to the Promise returned by call(). Only the leading execution's errors affect
 * the returned Promise.
 *
 * **Cancellation**: Calling cancel() clears pending executions and resets leading edge state,
 * allowing immediate execution on next call().
 *
 * @param createPromise - Function that creates the Promise to execute
 * @param options - Configuration with waitMs delay and timing mode
 * @returns Object with call(), cancel(), flush() methods and isPending/lastResult getters
 *
 * @example
 * // Trailing: Execute after user stops typing
 * const debouncedSearch = debounce(() => searchAPI(query), { waitMs: 300 })
 * debouncedSearch.call() // Resets timer
 * debouncedSearch.call() // Resets timer again
 * // ... 300ms later → executes once
 *
 * @example
 * // Leading: Execute immediately, block repeats
 * const debouncedSubmit = debounce(() => submitForm(), { waitMs: 1000, timing: 'leading' })
 * await debouncedSubmit.call() // Executes immediately
 * await debouncedSubmit.call() // Returns cached result (within 1000ms)
 * // ... 1000ms later
 * await debouncedSubmit.call() // Executes again
 *
 * @example
 * // Both: Immediate + trailing
 * const debouncedUpdate = debounce(() => updateServer(), { waitMs: 500, timing: 'both' })
 * await debouncedUpdate.call() // Executes immediately (leading)
 * // ... 500ms later → executes again (trailing, errors don't affect leading Promise)
 */
export const debounce = <A>(
  createPromise: () => Promise<A>,
  options: {
    readonly waitMs: number
    readonly timing?: 'trailing' | 'leading' | 'both'
  }
) => {
  const { waitMs, timing = 'trailing' } = options

  // State management for debouncing behavior
  let timeoutId: number | undefined // Timer handle for delayed execution
  let lastResult: A | undefined // Cache the last successful result
  let isPending = false // Track if an execution is scheduled/running
  let hasExecutedLeading = false // For leading edge: prevent multiple executions

  /**
   * Clean up any pending timeouts and reset state
   * Called before each new execution to prevent overlapping
   */
  const cleanup = () => {
    if (timeoutId !== undefined) {
      clearTimeout(timeoutId)
      timeoutId = undefined
    }
    isPending = false
  }

  /**
   * Execute the user's Promise and handle the result
   * Wraps the provided Promise in async handling for debouncing
   */
  const executePromise = (): Promise<A> => {
    return createPromise()
      .then((result) => {
        lastResult = result // Cache successful result
        return result
      })
      .catch((error) => {
        // Capture debounce execution error for monitoring
        captureEffectError(error as Error, {
          operation: 'debounce:execute',
          event: 'debounce_execution_failed',
          payload: { waitMs, timing }
        })
        throw error
      })
  }

  /**
   * Schedule execution based on timing strategy
   * Handles the three timing modes: leading, trailing, both
   *
   * @param immediate - Force immediate execution (used by flush())
   */
  const scheduleExecution = (immediate = false): Promise<A> => {
    // Always clean up previous execution before starting new one
    cleanup()

    // LEADING or IMMEDIATE execution
    if (immediate || timing === 'leading') {
      // Leading edge protection: only execute once until quiet period ends
      if (timing === 'leading' && hasExecutedLeading) {
        // IMPORTANT: Return cached result to prevent re-execution during quiet period
        // This means rapid calls will receive the SAME result from the first execution
        // until waitMs passes without any calls. This is intentional to prevent
        // expensive operations from running multiple times (e.g., API calls, form submissions)
        return Promise.resolve(lastResult as A)
      }

      // Mark as executed and pending for leading edge
      hasExecutedLeading = true
      isPending = true

      // Execute immediately and handle post-execution cleanup
      const promise = executePromise()
        .then((result) => {
          isPending = false

          // For leading edge: schedule reset after wait period
          if (timing === 'leading') {
            timeoutId = window.setTimeout(() => {
              hasExecutedLeading = false // Allow next leading execution
              timeoutId = undefined
            }, waitMs)
          }

          return result
        })
        .catch((error) => {
          isPending = false
          throw error
        })

      // BOTH timing: execute now (leading) AND schedule trailing execution
      if (timing === 'both') {
        timeoutId = window.setTimeout(() => {
          isPending = true
          executePromise()
            .then(() => {
              isPending = false
            })
            .catch((error) => {
              isPending = false

              // IMPORTANT: Trailing execution errors are captured to Sentry but do NOT
              // propagate to the Promise returned by call(). The leading execution above
              // returns its own Promise that's independent from this trailing execution.
              // This means if trailing fails, the leading Promise still succeeds.
              captureEffectError(error as Error, {
                operation: 'debounce:trailing',
                event: 'debounce_trailing_failed',
                payload: { waitMs, timing }
              })
            })
        }, waitMs)
      }

      return promise
    }

    // TRAILING execution (default behavior)
    // Wait for the specified delay, then execute
    return new Promise((resolve, reject) => {
      isPending = true
      timeoutId = window.setTimeout(() => {
        isPending = true // Mark as executing
        executePromise()
          .then((result) => {
            isPending = false
            resolve(result)
          })
          .catch((error) => {
            isPending = false

            // Capture trailing execution error for monitoring
            captureEffectError(error as Error, {
              operation: 'debounce:trailing',
              event: 'debounce_trailing_execution_failed',
              payload: { waitMs, timing }
            })

            reject(error)
          })
      }, waitMs)
    })
  }

  return {
    /**
     * Execute the debounced Promise operation
     */
    call: (): Promise<A> => scheduleExecution(),

    /**
     * Cancel any pending execution
     */
    cancel: (): void => {
      cleanup()
      hasExecutedLeading = false
    },

    /**
     * Force immediate execution, canceling any pending operation
     */
    flush: (): Promise<A> => scheduleExecution(true),

    /**
     * Check if execution is pending
     */
    get isPending(): boolean {
      return isPending
    },

    /**
     * Get the last successful result
     */
    get lastResult(): A | undefined {
      return lastResult
    }
  }
}

/**
 * Debounced LiveView push operations with built-in error handling
 */
export const debounceLiveViewPush = <T = unknown>(
  hook: ViewHook,
  event: string,
  options: {
    readonly waitMs: number
    readonly timing?: 'trailing' | 'leading' | 'both'
    readonly target?: string
    readonly errorMessage?: string
  }
) => {
  const { target, errorMessage, ...debounceOptions } = options

  // State to track the latest payload for proper debouncing
  let latestPayload: Record<string, unknown> = {}

  // Create a single debounced promise that uses the latest payload
  // This ensures proper debouncing behavior while handling dynamic payloads
  const debounced = debounce(
    () => pushToLiveView<T>(hook, event, latestPayload, target),
    debounceOptions
  )

  return {
    /**
     * Execute a debounced LiveView push with the given payload
     * Updates the latest payload and triggers the shared debounced promise
     */
    call: (payload: Record<string, unknown> = {}): Promise<T> => {
      // Update the latest payload for the debounced execution
      latestPayload = payload

      return debounced.call().catch((error) => {
        // Log user-friendly error message if provided
        if (errorMessage) {
        }

        // Capture error with rich context for debugging
        captureEffectError(error, {
          component: hook.el?.dataset?.component || hook.el?.tagName,
          hookElement: hook.el,
          event,
          payload,
          target,
          operation: 'debounceLiveViewPush'
        })

        // Re-throw for caller to handle
        throw error
      })
    },

    /**
     * Cancel any pending executions
     * Note: Only affects the base debounced promise, not individual call() promises
     */
    cancel: (): void => debounced.cancel(),

    /**
     * Force immediate execution with the given payload
     * Bypasses debouncing by setting waitMs to 0
     */
    flush: (payload: Record<string, unknown> = {}): Promise<T> => {
      // Update the latest payload and force immediate execution
      latestPayload = payload
      return debounced.flush()
    },

    /**
     * Check if any execution is currently pending
     * Uses the base debounced promise's state
     */
    get isPending(): boolean {
      return debounced.isPending
    },

    /**
     * Convenience method to execute a debounced push with automatic error handling
     * Wraps call() with silent error handling
     */
    execute: (payload: Record<string, unknown> = {}): void => {
      // Update the latest payload and trigger the shared debounced promise
      latestPayload = payload

      debounced.call().catch((error) => {
        // Log user-friendly error message if provided
        if (errorMessage) {
        }

        // Capture error with rich context for debugging
        captureEffectError(error, {
          component: hook.el?.dataset?.component || hook.el?.tagName,
          hookElement: hook.el,
          event,
          payload,
          target,
          operation: 'debounceLiveViewPush.execute'
        })

        // Silent error handling - errors are already logged
      })
    }
  }
}

/**
 * File upload with progress tracking, retry logic, and comprehensive error handling
 * Promise-based implementation (migrated from Effect.ts)
 */
export const uploadFile = (
  entry: {
    file: File
    meta: { url: string; fields: Record<string, string> }
    progress: (percent: number) => void
    error: (message?: string) => void
  },
  options: {
    readonly timeout?: number
    readonly retries?: number
    readonly progressCallback?: (percent: number) => void
    readonly errorCallback?: (error: UploadError) => void
    readonly abortController?: AbortController
  } = {}
): Promise<void> => {
  const {
    timeout = 60_000,
    retries = 2,
    progressCallback,
    errorCallback,
    abortController
  } = options

  // Single upload attempt (no retry)
  const attemptUpload = (): Promise<void> =>
    new Promise((resolve, reject) => {
      const formData = new FormData()
      const { url, fields } = entry.meta

      // Add S3 fields to form data
      for (const [key, val] of Object.entries(fields)) {
        formData.append(key, val)
      }
      formData.append('file', entry.file)

      const xhr = new XMLHttpRequest()

      // Setup abort handling
      if (abortController) {
        abortController.signal.addEventListener('abort', () => {
          xhr.abort()
          reject(
            new UploadError(
              entry.file.name,
              entry.file.size,
              'Upload was cancelled by user',
              undefined,
              'aborted'
            )
          )
        })
      }

      // Progress tracking
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const percent = Math.round((event.loaded / event.total) * 100)

          // Call both callbacks for compatibility
          if (percent < 100) {
            entry.progress(percent)
            progressCallback?.(percent)
          }
        }
      })

      // Success handling
      xhr.onload = () => {
        if (xhr.status === 204 || (xhr.status >= 200 && xhr.status < 300)) {
          entry.progress(100)
          progressCallback?.(100)
          resolve()
        } else {
          const error = new UploadError(
            entry.file.name,
            entry.file.size,
            `Upload failed with status ${xhr.status}: ${xhr.statusText}`,
            xhr.status,
            xhr.responseText || 'HTTP error'
          )

          // Capture HTTP error immediately for monitoring
          captureEffectError(error, {
            operation: 'uploadFile',
            event: 'upload_http_error',
            payload: {
              fileName: error.fileName,
              fileSize: error.fileSize,
              status: error.status,
              statusText: xhr.statusText
            }
          })

          entry.error(error.message)
          errorCallback?.(error)
          reject(error)
        }
      }

      // Error handling
      xhr.onerror = () => {
        const error = new UploadError(
          entry.file.name,
          entry.file.size,
          `Network error uploading ${entry.file.name}`,
          undefined,
          'network_error'
        )

        // Capture network error immediately for monitoring
        captureEffectError(error, {
          operation: 'uploadFile',
          event: 'upload_network_error',
          payload: {
            fileName: error.fileName,
            fileSize: error.fileSize
          }
        })

        entry.error(error.message)
        errorCallback?.(error)
        reject(error)
      }

      // Timeout handling
      xhr.ontimeout = () => {
        const error = new UploadError(
          entry.file.name,
          entry.file.size,
          `Upload timeout for ${entry.file.name}`,
          undefined,
          'timeout'
        )

        // Capture timeout error immediately for monitoring
        captureEffectError(error, {
          operation: 'uploadFile',
          event: 'upload_timeout',
          payload: {
            fileName: error.fileName,
            fileSize: error.fileSize,
            timeout
          }
        })

        entry.error(error.message)
        errorCallback?.(error)
        reject(error)
      }

      // Start upload with error handling
      try {
        xhr.open('POST', url, true)
        xhr.timeout = timeout
        xhr.send(formData)
      } catch (error) {
        const uploadError = new UploadError(
          entry.file.name,
          entry.file.size,
          `Failed to initiate upload: ${String(error)}`,
          undefined,
          error
        )

        // Capture upload initiation error for monitoring
        captureEffectError(uploadError, {
          operation: 'uploadFile',
          event: 'upload_initiation_failed',
          payload: {
            fileName: uploadError.fileName,
            fileSize: uploadError.fileSize,
            url
          }
        })

        entry.error(uploadError.message)
        errorCallback?.(uploadError)
        reject(uploadError)
        return
      }

      // Add breadcrumb for debugging
      if (process.env.NODE_ENV === 'production') {
        Sentry.addBreadcrumb({
          category: 'upload',
          message: `Starting upload: ${entry.file.name}`,
          level: 'info',
          data: {
            fileName: entry.file.name,
            fileSize: entry.file.size,
            timeout
          }
        })
      }
    })

  // Retry logic with exponential backoff
  const uploadWithRetry = async (): Promise<void> => {
    let lastError: UploadError | undefined

    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        await attemptUpload()
        return // Success!
      } catch (error) {
        lastError = error as UploadError

        // Check if upload was aborted - no point in retrying
        if (abortController?.signal.aborted) {
          throw lastError
        }

        // If this is not the last attempt, wait before retrying
        if (attempt < retries) {
          // Exponential backoff: 1s, 2s, 4s, etc.
          const delayMs = 1000 * 2 ** attempt
          await new Promise((resolve) => setTimeout(resolve, delayMs))
        }
      }
    }

    // All retries exhausted, capture and throw final error
    if (lastError) {
      captureEffectError(lastError, {
        operation: 'uploadFile',
        event: 'upload_failed',
        payload: {
          fileName: lastError.fileName,
          fileSize: lastError.fileSize,
          status: lastError.status
        }
      })
      throw lastError
    }
  }

  return uploadWithRetry()
}

/**
 * Enhanced S3 uploader with error handling and progress tracking
 */
export const enhancedS3Uploader = (
  entries: Array<{
    file: File
    meta: { url: string; fields: Record<string, string> }
    progress: (percent: number) => void
    error: (message?: string) => void
  }>,
  onViewError: (cleanup: () => void) => void
) => {
  // Track active uploads for cleanup
  const abortControllers: AbortController[] = []

  // Setup view error cleanup
  onViewError(() => {
    for (const controller of abortControllers) {
      controller.abort()
    }
  })

  // Process each entry with Promise-based upload
  for (const entry of entries) {
    const abortController = new AbortController()
    abortControllers.push(abortController)

    uploadFile(entry, {
      timeout: 60_000,
      retries: 2,
      abortController,
      errorCallback: (_error) => {}
    }).catch((error) => {
      // Capture any unhandled upload errors for monitoring
      captureEffectError(error, {
        operation: 'enhancedS3Uploader',
        event: 'upload_promise_rejection',
        payload: {
          fileName: entry.file.name,
          fileSize: entry.file.size
        }
      })

      // Error already handled by uploadFile and reported to Sentry
      // This catch prevents unhandled promise rejection
    })
  }
}
